import { t } from '@lingui/core/macro';
import type { AddressFallbackRecord, UserGroupAddressRecord } from '@navio/nsdk';
import { AddressLabel } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiButton, NoddiTextArea } from 'noddi-ui-web';
import { CountryCode } from 'noddi-util';
import { AddressPicker, getShortAddressName } from 'noddi-web';
import { useState } from 'react';
import { buildManualProfileAddressBody, isAddressFallbackComplete } from '../../utils/addressFallback';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { InlineRemovalConfirm } from './InlineRemovalConfirm';
import { PERSONAL_INFO_REVIEW_MUTATION_KEY, type InstructionsDraft } from './types';

type Props = {
  userGroupId: number;
  userGroupAddresses: UserGroupAddressRecord[];
  instructions: InstructionsDraft;
  onAddressResolutionChange: (isResolving: boolean) => void;
  onInstructionsChange: (userGroupAddressId: number, value: string) => void;
};

export const ConfirmAddressesStep = ({
  userGroupId,
  userGroupAddresses,
  instructions,
  onAddressResolutionChange,
  onInstructionsChange
}: Props) => {
  const [addressIdPendingRemoval, setAddressIdPendingRemoval] = useState<number | null>(null);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [openNoteIds, setOpenNoteIds] = useState<number[]>([]);

  const {
    mutate: removeAddresses,
    isPending: isRemoving,
    error: removeError
  } = noddiAsync.usePost({
    type: URLKeys.postDeleteUserGroupAddresses,
    queryConfig: {
      mutationKey: PERSONAL_INFO_REVIEW_MUTATION_KEY,
      // Await the refetch before clearing: dropping the pending state first leaves the removed row
      // on screen, removable a second time and still carrying a note into the final confirm.
      onSuccess: async () => {
        await invalidateQuery({ urlKey: URLKeys.getUserGroupAddresses });
        setAddressIdPendingRemoval(null);
      }
    }
  });

  return (
    <div className='flex flex-col gap-4'>
      {/* One bordered list with divided rows — a card per address turned five addresses into a wall of boxes. */}
      <div className='divide-y divide-border overflow-hidden rounded-lg border border-border'>
        {userGroupAddresses.map((userGroupAddress) => {
          const { address } = userGroupAddress;
          const note = instructions[userGroupAddress.id] ?? userGroupAddress.instructions;
          const isNoteOpen = openNoteIds.includes(userGroupAddress.id) || note !== '';
          const isPendingRemoval = addressIdPendingRemoval === address.id;

          return (
            <div key={userGroupAddress.id} className='flex flex-col gap-2 p-4'>
              <div className='flex items-start justify-between gap-3'>
                <div className='flex min-w-0 items-baseline gap-2'>
                  <NoddiIcon name='MapPin' size='small' />
                  <p className='min-w-0'>
                    <span className='font-semibold'>{getShortAddressName(address)}</span>{' '}
                    <span className='text-muted-foreground'>
                      {address.zipCode} {address.city}
                    </span>
                  </p>
                </div>
                {!isPendingRemoval && (
                  <NoddiButton
                    className='shrink-0'
                    variant='ghost'
                    size='small'
                    startIcon='Trash2'
                    aria-label={`${t`Remove`}: ${getShortAddressName(address)}`}
                    onClick={() => setAddressIdPendingRemoval(address.id)}
                  >
                    {t`Remove`}
                  </NoddiButton>
                )}
              </div>

              {isPendingRemoval ? (
                <InlineRemovalConfirm
                  question={t`Remove this address?`}
                  error={removeError}
                  isRemoving={isRemoving}
                  onCancel={() => setAddressIdPendingRemoval(null)}
                  onConfirm={() => removeAddresses({ userGroupId, addressIds: [address.id] })}
                />
              ) : isNoteOpen ? (
                <NoddiTextArea
                  className='min-w-0'
                  aria-label={t`Note for the driver`}
                  placeholder={t`For example: parked in the garage, code 1234`}
                  value={note}
                  onChange={(event) => onInstructionsChange(userGroupAddress.id, event.target.value)}
                />
              ) : (
                <NoddiButton
                  className='self-start no-underline'
                  variant='link'
                  size='small'
                  startIcon='Plus'
                  onClick={() => setOpenNoteIds((current) => [...current, userGroupAddress.id])}
                >
                  {t`Note for the driver`}
                </NoddiButton>
              )}
            </div>
          );
        })}
      </div>

      {isAddingAddress ? (
        <AddAddressCard
          userGroupId={userGroupId}
          onAddressResolutionChange={onAddressResolutionChange}
          onClose={() => {
            onAddressResolutionChange(false);
            setIsAddingAddress(false);
          }}
        />
      ) : (
        <NoddiButton className='self-start' variant='outline' startIcon='Plus' onClick={() => setIsAddingAddress(true)}>
          {t`Add an address`}
        </NoddiButton>
      )}
    </div>
  );
};

const AddAddressCard = ({
  userGroupId,
  onAddressResolutionChange,
  onClose
}: {
  userGroupId: number;
  onAddressResolutionChange: (isResolving: boolean) => void;
  onClose: () => void;
}) => {
  const [selectedAddress, setSelectedAddress] = useState<{ id: number; name: string } | undefined>();
  const [addressFallback, setAddressFallback] = useState<AddressFallbackRecord | null>(null);

  const {
    mutate: createUserGroupAddress,
    isPending: isSaving,
    error: createError
  } = noddiAsync.usePost({
    type: URLKeys.postUserGroupAddress,
    queryConfig: {
      mutationKey: PERSONAL_INFO_REVIEW_MUTATION_KEY,
      onSuccess: async () => {
        await invalidateQuery({ urlKey: URLKeys.getUserGroupAddresses });
        onClose();
      }
    }
  });

  const canSave = selectedAddress != null || isAddressFallbackComplete(addressFallback);

  const handleSave = () => {
    if (selectedAddress) {
      createUserGroupAddress({ userGroupId, addressId: selectedAddress.id, label: AddressLabel.HOME });
      return;
    }
    if (!addressFallback || !isAddressFallbackComplete(addressFallback)) {
      return;
    }
    // Atomic: the backend get-or-creates the address and attaches it in one transaction.
    createUserGroupAddress({
      userGroupId,
      label: AddressLabel.HOME,
      address: buildManualProfileAddressBody(addressFallback, CountryCode.NORWAY)
    });
  };

  return (
    <div className='flex flex-col gap-3 rounded-lg border border-border p-4'>
      {createError && <ApiErrorMessageWithTrans error={createError} />}

      <AddressPicker
        hideLabel
        enableFallback
        skipIsInDeliveryAreaCheck
        address={selectedAddress}
        onAddressCreation={setSelectedAddress}
        onAddressResolutionChange={onAddressResolutionChange}
        onFallbackChange={setAddressFallback}
        translations={getCommonTranslations().addressSearch}
        fallbackTranslations={{
          cantFindAddress: t`Can't find your address? Enter it manually`,
          backToSearch: t`Back to address search`,
          address: t`Address`,
          addressPlaceholder: t`Address`,
          streetNumber: t`Number`,
          streetNumberPlaceholder: t`Nr.`,
          zipcode: t`Zipcode`,
          zipcodePlaceholder: t`Zipcode`,
          city: t`City`,
          cityPlaceholder: t`City`
        }}
      />

      <div className='flex justify-end gap-2'>
        <NoddiButton variant='outline' size='small' onClick={onClose}>
          {t`Cancel`}
        </NoddiButton>
        <NoddiButton size='small' loading={isSaving} disabled={!canSave} onClick={handleSave}>
          {t`Save address`}
        </NoddiButton>
      </div>
    </div>
  );
};
